import { ComponentProps, FC, useEffect } from 'react';
import { Field } from 'redux-form';

import ThemedFormInput from 'inquiry/components/ThemedFormInput';
import { parseEmptyStringToNull } from 'inquiry/lib/form';
import { useApply, useValidators } from 'inquiry/lib/jsonLogic';
import { useShouldShowServerError } from 'inquiry/lib/serverError';
import { InputTextComponent, InputType } from 'lib/interfaces/ui-components';
import isEmpty from 'lib/utils/formValidation/isEmpty';
import { UiComponentActionsApi } from 'verify/lib/interfaces/uiComponent';
import { Inquiry } from 'verify/models/inquiry';

export const INPUT_TYPE_MAP: Record<
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  InputTextComponent['attributes']['input-type'],
  ComponentProps<'input'>['type']
> = {
  [InputType.Text]: 'text',
  [InputType.Email]: 'email',
  [InputType.NumberPad]: 'number',
};

interface InputTextProps {
  component: InputTextComponent;
  inquiry: Inquiry;
  actionsApi: UiComponentActionsApi;
  inquiryLoading: boolean;
}

const InputText: FC<InputTextProps> = ({
  component,
  inquiry,
  actionsApi,
  inquiryLoading,
}: InputTextProps) => {
  const {
    'input-type': inputType = InputType.Text,
    disabled,
    hidden,
    label,
    optional,
    placeholder,
    prefill,
    validations,
    error,
  } = component.attributes;

  const formField = component.name;

  const shouldShowServerError = useShouldShowServerError(
    inquiry,
    inquiryLoading,
    component.name,
    prefill,
  );

  const apply = useApply(formField, inquiry.attributes['next-step'].name);
  const validators = useValidators({ hidden, optional, validations });

  // TODO: figure out how to test this
  const isHidden = apply<boolean>(hidden);

  useEffect(() => {
    if (isHidden) {
      // Setting to '' removes the field value from form state entirely, unlike null which sets value to null.
      actionsApi.setField(formField, '');
    } else if (!isHidden && !isEmpty(prefill)) {
      actionsApi.setField(formField, prefill);
    }
  }, [isHidden]);

  if (isHidden) {
    return null;
  }

  return (
    <Field
      name={formField}
      id={formField}
      component={ThemedFormInput}
      disabled={apply<boolean>(disabled)}
      parse={parseEmptyStringToNull}
      validate={validators}
      label={label}
      placeholder={placeholder}
      type={INPUT_TYPE_MAP[inputType]}
      serverError={shouldShowServerError ? error : null}
      styles={component.styles}
    />
  );
};

export default InputText;
